// Sustemia · Balance Hídrico · glue entre el documento persistido, lo
// heredado de Agua/Production/Social, y el motor de cálculo. No es catálogo
// ni motor: es la traducción entre las tres cosas para la UI.
//
// Los nombres de campo del estado local (y del documento persistido) están
// en inglés, alineados con la convención de Water/Production/Social. El
// motor (waterBalanceCalc.js) sigue hablando en español puertas adentro —
// nunca persiste nada, así que no necesita alinearse con "la base". Por eso
// `buildEstadoParaMotor` es literalmente un traductor: entra en inglés,
// sale en el vocabulario que ya esperan las funciones del motor.

import { gradoDe, gradoConAptitudInstrumento, medidorAguasAbajoValido } from "./waterBalanceCalc";
import { calcularCasoB2 } from "./waterBalanceCalc";
import { aptoParaFluido } from "./catalogs/instrumentos";

export function estadoInicial() {
  return {
    typology: null,
    hasLaundry: true,
    // operatingDays y staffing NO se guardan acá: se derivan siempre de
    // Social (leerDiasOperativosDelPeriodo/leerDotacionDelPeriodo). Lo único
    // propio del Balance en el contexto es lo que no existe en ningún otro
    // módulo: geoNote y riesgo hídrico.
    context: { geoNote: "", wriRisk: "", wwfRisk: "" },
    sourceAnnotations: [],
    centers: [],
    instruments: [],
    outputs: [],
    storage: [],
    executedFieldCases: [],
    lossClassification: [],
    divergences: [],
    correctionProposals: [],
    costs: { tariffs: { waterPerM3: "", thermalEnergyPerM3: "", chemicalsPerM3: "", treatmentPerM3: "" }, perCenter: [] },
    closed: false,
  };
}

export function toLocalState(balance) {
  if (!balance) return estadoInicial();
  const base = estadoInicial();
  return {
    ...base,
    ...balance,
    context: { ...base.context, ...(balance.context || {}) },
    costs: {
      ...base.costs,
      ...(balance.costs || {}),
      tariffs: { ...base.costs.tariffs, ...(balance.costs?.tariffs || {}) },
    },
  };
}

/** Payload a mandar al backend — sólo lo editable, nada derivado. */
export function toPayload(local, { site, period, year }) {
  return {
    site,
    period,
    year,
    typology: local.typology,
    hasLaundry: local.hasLaundry,
    context: local.context,
    sourceAnnotations: local.sourceAnnotations,
    centers: local.centers,
    instruments: local.instruments,
    outputs: local.outputs,
    storage: local.storage,
    executedFieldCases: local.executedFieldCases,
    lossClassification: local.lossClassification,
    divergences: local.divergences,
    correctionProposals: local.correctionProposals,
    costs: local.costs,
    closed: local.closed,
  };
}

export function isCalibrationCurrent(date) {
  if (!date) return false;
  return new Date(date) > new Date();
}

function tankDelta(tank) {
  if (!tank?.geometry?.diameter || tank.initialLevel == null || tank.finalLevel == null) return 0;
  const { deltaInventarioM3 } = calcularCasoB2({
    diametro: tank.geometry.diameter,
    nivelInicial: tank.initialLevel,
    nivelFinal: tank.finalLevel,
  });
  return deltaInventarioM3;
}

/**
 * Arma el estado que consume `recalcularBalance()` a partir del documento
 * local (editable, en inglés) y lo heredado de Agua/Production/Social. El
 * motor sigue esperando su vocabulario propio en español (entradas, salidas,
 * grado, volumenM3...): esta función es la traducción, no una excepción.
 */
export function buildEstadoParaMotor(local, heredado) {
  const entradas = (heredado?.inputs || [])
    .filter((f) => typeof f.volumeM3 === "number")
    .map((f) => ({
      id: f.sourceId,
      nombre: f.sourceId,
      volumenM3: f.volumeM3,
      grado: gradoDe(f.declaredMethod, isCalibrationCurrent(f.calibrationDue), f.hasEvidence),
    }));

  const salidas = (local.outputs || []).map((s) => {
    // Motor de cálculo §2/§8: un instrumento no apto para su fluido topa el
    // grado en 3 (Fuentes.md §3: "una turbina sobre efluente se traba y da
    // de menos, el certificado no lo compensa"), y uno con la calibración
    // vencida degrada lo que depende de él — nunca se asume vigente por
    // default cuando hay un instrumento real vinculado (ver InstrumentosStep).
    const instrumento = s.instrumentId ? (local.instruments || []).find((i) => i.id === s.instrumentId) : null;
    const calibracionVigente = instrumento ? isCalibrationCurrent(instrumento.calibrationDue) : true;
    const gradoBase = s.validityGrade ?? gradoDe(s.origin, calibracionVigente, (s.evidence || []).length > 0);
    const apto = instrumento ? aptoParaFluido(instrumento.instrumentType, instrumento.fluid) : true;
    return {
      id: s.centerId || s.name,
      nombre: s.name,
      volumenM3: s.volumeM3 ?? 0,
      pendiente: s.volumeM3 == null,
      grado: gradoConAptitudInstrumento(gradoBase, apto),
      // origin "reparto" = un volumen sacado por proxy de un medidor
      // compartido que ya está declarado en otra salida: no suma al balance
      // (Fuentes.md §3, "no cuenta dos veces"). soloInformativo lo excluye de
      // totalSalidas/pareto/índice de calidad en recalcularBalance, aunque
      // sigue apareciendo en coberturaDeSalidas y en el costeo por centro.
      soloInformativo: s.origin === "reparto",
    };
  });

  const deltaInventarioM3 = (local.storage || []).reduce((a, t) => a + tankDelta(t), 0);

  // V16: un instrumento con padre declarado y rol distinto de interno cuenta
  // dos veces un volumen que ya pasó por el macromedidor — bloquea el cierre
  // (ver medidorAguasAbajoValido en waterBalanceCalc.js).
  const dobleConteoInstrumentos = (local.instruments || []).some(
    (i) => !medidorAguasAbajoValido({ medidorPadreId: i.parentMeterId, rolMedidor: i.meterRole })
  );

  return {
    entradas,
    salidas,
    deltaInventarioM3,
    cerrado: !!local.closed,
    clasificacionResiduo: (local.lossClassification || []).map((c) => ({ categoria: c.category, volumenM3: c.volumeM3, nota: c.note })),
    divergenciasSinResolver: (local.divergences || []).filter((d) => d.status === "sin_resolver").length,
    dobleConteoInstrumentos,
    benchmark: null,
  };
}

export default { estadoInicial, toLocalState, toPayload, isCalibrationCurrent, buildEstadoParaMotor };
